import type { z } from 'zod';

import type { MigrationContext } from './types';

export type EntityVersionDef<TSchema extends z.ZodType = z.ZodType> = {
    schema: TSchema;
    /** Migrate from the previous version's shape. Absent for the initial version. */
    up?: (old: any, ctx?: MigrationContext) => unknown;
};

/**
 * A pre-versioning ("legacy") shape of an entity. One entity may have several legacy
 * variants converging into the same versioned shape (e.g. the websites and mini-landing
 * forms of the same widget). Variants are tried in declaration order and the first whose
 * schema matches wins — keep schemas mutually discriminating (e.g. a literal `type` field).
 */
export type LegacyVariantDef = {
    /** Identifies the variant in error messages, e.g. 'web', 'ml'. */
    name: string;
    /** Loose shape-identification schema for this legacy form. */
    schema: z.ZodType;
    /** Transform to a versioned shape; MUST stamp a numeric `version` (normally 0). */
    up: (old: any, ctx?: MigrationContext) => unknown;
};

type EntityVersionMap = Record<number, EntityVersionDef>;

export type VersionedEntityParseResult<T> = { type: 'ok'; value: T } | { type: 'err'; error: unknown };

export type VersionedEntitySchema<M extends EntityVersionMap, L extends keyof M & number = keyof M & number> = {
    safeParse: (data: unknown, ctx?: MigrationContext) => VersionedEntityParseResult<z.output<M[L]['schema']>>;
    latestSchema: M[L]['schema'];
    latestVersion: L;
    versionMap: M;
    legacy?: readonly LegacyVariantDef[];
    getVersion: (data: unknown) => number | null;
};

/** Entity type of the latest version of a versioned entity schema. */
export type InferredEntity<T extends { latestSchema: z.ZodType }> = z.output<T['latestSchema']>;

const defaultGetVersion = (data: unknown): number | null => {
    if (typeof data !== 'object' || data === null) return null;
    const version = (data as { version?: unknown }).version;
    return typeof version === 'number' ? version : null;
};

/**
 * Creates a versioned entity schema: detects the version of incoming data, validates it
 * against that version's schema, migrates it up to the latest version, and validates the
 * final result. Data without a determinable version is matched against the optional
 * `legacy` variants (first matching schema wins) and migrated into the versioned chain;
 * entities without `legacy` reject such data.
 */
export const createVersionedEntityWithDefaults = <M extends EntityVersionMap, L extends keyof M & number>(input: {
    versionMap: M;
    /** Required and explicit: an inferred latest would type latestSchema as a union of all versions. */
    latestVersion: L;
    legacy?: LegacyVariantDef[];
    getVersion?: (data: unknown) => number | null;
}): VersionedEntitySchema<M, L> => {
    const { versionMap, latestVersion, legacy } = input;
    const getVersion = input.getVersion ?? defaultGetVersion;
    const latestSchema = versionMap[latestVersion].schema as M[L]['schema'];

    const resolveLegacy = (data: unknown, ctx?: MigrationContext): VersionedEntityParseResult<unknown> => {
        if (!legacy?.length) {
            return { type: 'err', error: 'unable to determine entity version' };
        }

        for (const variant of legacy) {
            const parsed = variant.schema.safeParse(data);
            if (parsed.success) {
                return { type: 'ok', value: variant.up(parsed.data, ctx) };
            }
        }

        const tried = legacy.map((variant) => variant.name).join(', ');
        return { type: 'err', error: `no legacy variant matched (tried: ${tried})` };
    };

    const safeParse = (data: unknown, ctx?: MigrationContext): VersionedEntityParseResult<z.output<M[L]['schema']>> => {
        let current: unknown = data;
        let version = getVersion(current);

        if (version === null) {
            const resolved = resolveLegacy(current, ctx);
            if (resolved.type !== 'ok') {
                return resolved as VersionedEntityParseResult<z.output<M[L]['schema']>>;
            }

            current = resolved.value;
            // strict re-detection: a legacy up() that forgets to stamp `version` must fail here
            version = defaultGetVersion(current);
            if (version === null) {
                return { type: 'err', error: `legacy migration produced data without a version` };
            }
        }

        const versionDef = versionMap[version];
        if (!versionDef) {
            return { type: 'err', error: `unknown entity version ${version}` };
        }

        const parsed = versionDef.schema.safeParse(current);
        if (!parsed.success) {
            return { type: 'err', error: parsed.error };
        }

        if (version === latestVersion) {
            return { type: 'ok', value: parsed.data as z.output<M[L]['schema']> };
        }

        current = parsed.data;
        for (let next = version + 1; next <= latestVersion; next += 1) {
            const nextDef = versionMap[next];
            if (!nextDef?.up) {
                return { type: 'err', error: `missing migration to entity version ${next}` };
            }
            current = nextDef.up(current, ctx);
        }

        // validate the final shape so buggy up() migrations surface here, not downstream
        const migrated = latestSchema.safeParse(current);
        if (!migrated.success) {
            return { type: 'err', error: migrated.error };
        }

        return { type: 'ok', value: migrated.data as z.output<M[L]['schema']> };
    };

    return { safeParse, latestSchema, latestVersion, versionMap, legacy, getVersion };
};
